// Kiosk draft helpers (India Jackson 2026-11-03): her mother filled every
// form on the iPad, Submit did nothing, and no request ever reached the
// office. After a reload the kiosk made her start over because a draft
// only restored when the new queue listed the same ids in the same order.
// These helpers restore by form identity, survive a full localStorage,
// and report what happened so the next case is not a guess.
export const DRAFT_PREFIX = 'cervica_kiosk_draft_v1_';
export const PENDING_PREFIX = 'cervica_kiosk_pending_v1_';
export const DRAFT_TTL_MS = 4 * 60 * 60 * 1000;

export const queueKeyFor = (queue) => (queue || []).map((q) => `${q.type}:${q.data.id}`).join('|');
export const queueIdsFromKey = (key) => String(key || '').split('|').filter(Boolean).map((k) => { const i = k.indexOf(':'); return { type: k.slice(0, i), id: k.slice(i + 1) }; });

// Answers and signatures for every form or outcome present in both the
// draft and the new queue. Order does not matter; currentForm is kept only
// when the order is unchanged. Returns null when nothing overlaps.
export function restoreDraftForQueue(draft, queue) {
  if (!draft || !Array.isArray(queue) || queue.length === 0) return null;
  const draftIds = queueIdsFromKey(draft.queueKey);
  const draftKeys = new Set(draftIds.map((d) => `${d.type}:${d.id}`));
  const responses = draft.allResponses || {};
  const sigs = draft.signatures || {};
  const allResponses = {}; const signatures = {};
  let matched = 0;
  for (const q of queue) {
    const id = q.data.id;
    if (!draftKeys.has(`${q.type}:${id}`)) continue;
    if (responses[id] !== undefined) allResponses[id] = responses[id];
    if (sigs[id] !== undefined) signatures[id] = sigs[id];
    if (responses[id] !== undefined || sigs[id] !== undefined) matched++;
  }
  if (matched === 0) return null;
  const dropped = draftIds.filter((d) => !queue.some((q) => q.type === d.type && q.data.id === d.id)).length;
  const sameOrder = draft.queueKey === queueKeyFor(queue);
  const currentForm = sameOrder && typeof draft.currentForm === 'number' ? Math.min(draft.currentForm, queue.length - 1) : 0;
  return { allResponses, signatures, currentForm, matched, dropped };
}

function keysOf(storage) {
  const out = [];
  try { for (let i = 0; i < storage.length; i++) { const k = storage.key(i); if (k) out.push(k); } } catch { /* unavailable */ }
  return out;
}

export function storageUsageBytes(storage) {
  let total = 0;
  for (const k of keysOf(storage)) { try { total += k.length + String(storage.getItem(k) || '').length; } catch { /* skip */ } }
  return total;
}

export function draftCount(storage) {
  return keysOf(storage).filter((k) => k.startsWith(DRAFT_PREFIX)).length;
}

function isExpired(storage, key, now) {
  try { const saved = JSON.parse(storage.getItem(key))?.savedAt || 0; return now - saved > DRAFT_TTL_MS; } catch { return true; }
}

// Drop every other patient's draft and pending marker, and anything
// expired, so this patient's answers fit.
function pruneForPatient(storage, patientId, now) {
  for (const k of keysOf(storage)) {
    const own = k === DRAFT_PREFIX + patientId || k === PENDING_PREFIX + patientId;
    if (k.startsWith(DRAFT_PREFIX)) { if (!own || isExpired(storage, k, now)) { try { storage.removeItem(k); } catch { /* */ } } }
    else if (k.startsWith(PENDING_PREFIX) && !own) { try { storage.removeItem(k); } catch { /* */ } }
  }
}

// 'saved' | 'partial' (answers kept, signatures dropped) | 'failed'
export function saveDraftDurably(storage, patientId, payload, now = Date.now()) {
  if (!patientId) return 'failed';
  const key = DRAFT_PREFIX + patientId;
  const write = (p) => storage.setItem(key, JSON.stringify({ ...p, savedAt: now }));
  try { write(payload); return 'saved'; } catch { /* quota or unavailable */ }
  pruneForPatient(storage, patientId, now);
  try { write(payload); return 'saved'; } catch { /* still full */ }
  const { signatures, ...stripped } = payload || {};
  void signatures;
  try { write(stripped); return 'partial'; } catch { return 'failed'; }
}

// Fire and forget. The kiosk must never stall or throw because telemetry failed.
export function sendKioskDiagnostic(api, payload) {
  try {
    const p = api.post('/forms/kiosk-diagnostic', payload, { timeout: 5000 });
    if (p && typeof p.catch === 'function') p.catch(() => {});
  } catch { /* ignore */ }
}
